import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    FormsModule,
    NgControl,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {v4 as uuidv4} from 'uuid';
import {AppValidators, filterNulls, fromControl, TranslatePipe} from '@brunata-angular/util';
import {UppercaseDirective} from '@brunata-angular/ui';

import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {combineLatest, debounceTime, switchMap} from 'rxjs';
import {ReportingService} from 'src/app/reporting/reporting.service';

@Component({
    selector: 'app-country-postal-code-city',
    templateUrl: './country-postal-code-city.component.html',
    styleUrls: ['./country-postal-code-city.component.scss'],
    imports: [TranslatePipe, ReactiveFormsModule, FormsModule, NgbModule, UppercaseDirective],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountryPostalCodeCityComponent implements OnInit, AfterViewInit, ControlValueAccessor {
    @Input() editableCity = true;

    form: FormGroup<{
        country: FormControl<string>;
        postalCode: FormControl<string>;
        city: FormControl<string>;
    }> = this.fb.group({
        country: [''],
        postalCode: [''],
        city: [''],
    });

    id: string = uuidv4();
    required = false;
    private onChange: ((value: CountryPostalCodeCity | null) => void) | undefined;
    onTouch: (() => void) | undefined;

    constructor(
        private fb: NonNullableFormBuilder,
        @Self() public ngControl: NgControl,
        private reportingService: ReportingService,
    ) {
        ngControl.valueAccessor = this;
    }
    get labelClass(): string {
        return `${this.required ? 'required' : ''}`;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    ngOnInit(): void {
        this.form.statusChanges.subscribe(status => {
            if (status === 'PENDING') {
                this.ngControl.control?.markAsPending();
            } else {
                this.ngControl.control?.setErrors(this.form.errors);
            }
        });

        this.required = this.ngControl.control?.hasValidator(Validators.required) ?? false;

        if (this.editableCity) {
            combineLatest([
                fromControl(this.form.controls.country),
                this.form.controls.postalCode.valueChanges,
            ])
                .pipe(
                    debounceTime(500),
                    switchMap(([country, postalCode]) =>
                        this.reportingService.getCityFromCountryAndPostalCode(country, postalCode),
                    ),
                    filterNulls(),
                )
                .subscribe(city => {
                    this.form.controls.city.setValue(city);
                    this.updateModel();
                });
        } else {
            this.form.addAsyncValidators(
                AppValidators.postalCode(
                    this.reportingService.getCityFromCountryAndPostalCode,
                    city => this.form.controls.city.setValue(city ?? ''),
                    this.required,
                ),
            );
        }

        this.form.valueChanges.subscribe(() => {
            this.updateModel();
            if (this.onTouch !== undefined) {
                this.onTouch();
            }
        });
    }

    ngAfterViewInit(): void {
        // setTimeout fixes - "NG0100: Expression has changed after it was checked"
        setTimeout(() => {
            this.form.updateValueAndValidity();
            this.ngControl.control?.markAsUntouched();
        });
    }

    registerOnChange(fn: (value: CountryPostalCodeCity | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: CountryPostalCodeCity): void {
        this.form.patchValue(value);
    }

    private updateModel(): void {
        if (
            this.required &&
            (this.form.getRawValue().country === '' ||
                this.form.getRawValue().postalCode === '' ||
                this.form.getRawValue().city === '')
        ) {
            // If values are required, return nothing if one of the fields are missing.
            this.onChange?.(null);
        } else {
            this.onChange?.({
                country: this.form.getRawValue().country,
                postalCode: this.form.getRawValue().postalCode,
                city: this.form.getRawValue().city,
            });
        }
    }
}

export interface CountryPostalCodeCity {
    country: string;
    postalCode: string;
    city: string;
}
